import { useState, useRef, useEffect } from 'react';
import { api } from '../../lib/api';

interface BulkImageUploaderProps {
  onUploadComplete: (url: string) => void;
  onClose: () => void;
}

export default function BulkImageUploader({ onUploadComplete, onClose }: BulkImageUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progressText, setProgressText] = useState('');

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !uploading) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [uploading, onClose]);

  const compressImage = (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const img = new Image();
      img.onload = () => {
        const maxWidth = 1920;
        const maxHeight = 1080;
        let { width, height } = img as HTMLImageElement & { width: number; height: number };
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (blob) {
            const timestamp = Date.now();
            const originalName = file.name.replace(/\.[^/.]+$/, '');
            const compressedFile = new File([blob], `${originalName}_${timestamp}.webp`, {
              type: 'image/webp',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          }
        }, 'image/webp', 0.8);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFiles = async (files: FileList) => {
    const fileArr = Array.from(files);
    if (fileArr.length === 0) return;
    setUploading(true);
    try {
      let index = 0;
      for (const file of fileArr) {
        index += 1;
        setProgressText(`Processing ${index}/${fileArr.length}: ${file.name}`);
        // Basic size limit: 50MB
        const maxSize = 50 * 1024 * 1024;
        if (file.size > maxSize) {
          alert(`Skipping ${file.name}: file too large (>50MB)`);
          continue;
        }
        const processed = await compressImage(file);
        const data = await api.upload('/media/upload', processed);
        if (!data.success) {
          console.error('Upload error:', data.error);
          alert(`Upload failed for ${file.name}: ${data.error}`);
          continue;
        }
        
        onUploadComplete(data.url);
      }
      setProgressText('Done.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 cursor-pointer"
      onClick={() => { if (!uploading) onClose(); }}
    >
      <div
        className="bg-white rounded-lg p-6 w-full max-w-2xl cursor-default"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Bulk Image Upload</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-800 cursor-pointer">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">You can select multiple images. Images will be compressed and uploaded as WebP.</p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
          className="block w-full border rounded p-3"
          disabled={uploading}
        />
        <div className="mt-4 text-sm text-gray-700 min-h-6">{progressText}</div>
        <div className="mt-4 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Close</button>
        </div>
      </div>
    </div>
  );
}
